import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Search, Plus, Minus, Trash2, ShoppingBag, Check, Loader2, AlertTriangle, Store, Package, Receipt, Users, X, Pencil } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useRestaurantBySlug } from '../../lib/hooks';
import { usePOSRetailProducts } from '../../lib/menu-hooks';
import { useTenant } from '../../lib/tenant-context';
import { supabase } from '../../lib/supabase';
import { Skeleton } from '../../components/ui/Skeleton';
import { formatCurrency, cn } from '../../lib/utils';
import { PayTableModal } from '../../components/admin/PayTableModal';
import type { TableWithOrder } from '../../lib/types';
interface CartLine {
  id: string;
  menuItemId: string;
  code: string;
  name: string;
  unitPrice: number;
  quantity: number;
  stock?: number | null;
}
interface ActiveTable {
  orderId: string;
  tableNumber: string | number | null;
  customerName: string | null;
  total: number | null;
}
interface ProductForm {
  id: string | null;
  name: string;
  price: string;
  code: string;
  stock: string;
}
const EMPTY_PRODUCT_FORM: ProductForm = {
  id: null,
  name: '',
  price: '',
  code: '',
  stock: ''
};
function slugify(text: string): string {
  return text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60) || `producto-${Date.now()}`;
}
export function POSCajaPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const {
    restaurant,
    loading: restLoading
  } = useRestaurantBySlug(slug);
  const tenantCtx = useTenant();
  const isNatuCafe = String(restaurant?.id) === '19' || restaurant?.slug === 'natucafe-coffee-shop';
  const {
    items,
    loading: itemsLoading,
    refetch
  } = usePOSRetailProducts(restaurant?.id ? String(restaurant.id) : undefined);
  const [search, setSearch] = useState('');
  const [cart, setCart] = useState<CartLine[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  // Active-table picker
  const [showTablePicker, setShowTablePicker] = useState(false);
  const [activeTables, setActiveTables] = useState<ActiveTable[]>([]);
  const [tablesLoading, setTablesLoading] = useState(false);
  // ─── Pay modal (Venta rápida con factura/efectivo/tarjeta/SINPE) ──
  // En vez de insertar la orden ya cobrada (lo que rompía contra
  // orders_status_check), creamos la orden en status='pending' y
  // abrimos PayTableModal — el mismo modal de cerrar mesa, con
  // selección de método de pago + opción de factura. processPayment
  // se encarga de mover la orden a status='delivered' (canónico) y
  // de disparar el print job de factura para NatuCafé.
  const [payTable, setPayTable] = useState<TableWithOrder | null>(null);
  const [showPayModal, setShowPayModal] = useState(false);
  // ─── Product CRUD (Estantería) ────────────────────────────
  const [showProductForm, setShowProductForm] = useState(false);
  const [productForm, setProductForm] = useState<ProductForm>(EMPTY_PRODUCT_FORM);
  const [savingProduct, setSavingProduct] = useState(false);
  const [productError, setProductError] = useState<string | null>(null);
  // ─── Search filter ────────────────────────────────────────
  const visibleItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it: any) => {
      const name = (it.name || '').toLowerCase();
      const code = String(it.external_pos_id ?? '').toLowerCase();
      return name.includes(q) || code.includes(q);
    });
  }, [items, search]);
  // ─── Cart helpers ─────────────────────────────────────────
  const addToCart = (item: any) => {
    setError(null);
    setSuccessMsg(null);
    setCart(prev => {
      const existing = prev.find(l => l.menuItemId === String(item.id));
      if (existing) {
        return prev.map(l => l.id === existing.id ? {
          ...l,
          quantity: l.quantity + 1
        } : l);
      }
      return [...prev, {
        id: `${item.id}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        menuItemId: String(item.id),
        code: String(item.external_pos_id ?? ''),
        name: item.name,
        unitPrice: Number(item.price) || 0,
        quantity: 1,
        stock: item.stock_quantity === null || item.stock_quantity === undefined ? null : Number(item.stock_quantity)
      }];
    });
  };
  const updateQty = (lineId: string, delta: number) => {
    setCart(prev => prev.map(l => l.id === lineId ? {
      ...l,
      quantity: Math.max(0, l.quantity + delta)
    } : l).filter(l => l.quantity > 0));
  };
  const removeLine = (lineId: string) => {
    setCart(prev => prev.filter(l => l.id !== lineId));
  };
  const cartTotal = useMemo(() => cart.reduce((s, l) => s + l.unitPrice * l.quantity, 0), [cart]);
  const cartCount = useMemo(() => cart.reduce((s, l) => s + l.quantity, 0), [cart]);
  // ─── Quick sale: create a new order ───────────────────────
  const handleQuickSale = async () => {
    if (!restaurant?.id) return;
    if (cart.length === 0) {
      setError('Agrega al menos un producto al carrito.');
      return;
    }
    setSubmitting(true);
    setError(null);
    setSuccessMsg(null);
    try {
      const subtotal = cartTotal;
      const trackingToken = typeof crypto !== 'undefined' && (crypto as any).randomUUID ? (crypto as any).randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      // Crear la orden en status='pending' (valor canónico del
      // check constraint orders_status_check). El cobro se hace
      // adentro de PayTableModal — ahí processPayment la mueve a
      // 'delivered' al insertar el payment. source='pos_quick_sale'
      // marca la venta de mostrador (el trigger de print de cocina
      // ignora estas órdenes; sólo se imprime la factura al cobrar).
      const {
        data: orderRow,
        error: orderErr
      } = await supabase.from('orders').insert({
        restaurant_id: Number(restaurant.id),
        tracking_token: trackingToken,
        customer_name: 'Estantería',
        customer_phone: null,
        delivery_type: 'dine_in',
        payment_method: 'cash',
        status: 'pending',
        subtotal,
        delivery_fee: 0,
        total: subtotal,
        notes: 'Venta rápida Estantería',
        source: 'pos_quick_sale'
      }).select('id, order_number, tracking_token, restaurant_id, customer_name, customer_phone, delivery_type, payment_method, table_id, status, subtotal, delivery_fee, total, notes, source, created_at, updated_at').single();
      if (orderErr) throw orderErr;
      if (!orderRow?.id) throw new Error('No se pudo crear la orden.');
      // Insert order_items en 'pending' — el trigger de print de
      // cocina los ignora porque la orden tiene source='pos_quick_sale'
      // (guard en SETUP_NATUCAFE_PRINT_ON_ITEMS_TRIGGER.sql).
      const itemsPayload = cart.map(l => ({
        order_id: orderRow.id,
        restaurant_id: Number(restaurant.id),
        menu_item_id: l.menuItemId,
        item_name_snapshot: l.name,
        unit_price_snapshot: l.unitPrice,
        quantity: l.quantity,
        line_total: l.unitPrice * l.quantity,
        status: 'pending',
        notes: l.code ? `Cód. ${l.code}` : null
      }));
      const {
        error: itemsErr
      } = await supabase.from('order_items').insert(itemsPayload);
      if (itemsErr) throw itemsErr;
      // Construir TableWithOrder sintético — PayTableModal acepta
      // este shape (mismo patrón que CajaOverviewPage para órdenes
      // marketplace sin mesa real). table.id=null hace que el modal
      // cargue items vía orderId fallback.
      const syntheticTable: TableWithOrder = {
        id: '' as any,
        restaurant_id: String(restaurant.id),
        table_number: 'Estantería' as any,
        table_name: 'Estantería',
        zone: null,
        capacity: null,
        qr_code_url: null,
        qr_token: null,
        is_active: true,
        created_at: '',
        updated_at: '',
        current_order: {
          id: String(orderRow.id),
          restaurant_id: String(restaurant.id),
          order_number: String(orderRow.order_number || ''),
          tracking_token: orderRow.tracking_token,
          customer_name: orderRow.customer_name || 'Estantería',
          customer_phone: '',
          delivery_type: 'dine_in' as any,
          payment_method: 'cash',
          table_id: null,
          delivery_address: null,
          delivery_lat: null,
          delivery_lng: null,
          maps_url: null,
          subtotal: Number(orderRow.subtotal) || 0,
          delivery_fee: Number(orderRow.delivery_fee) || 0,
          total: Number(orderRow.total) || 0,
          status: 'pending' as any,
          notes: orderRow.notes,
          source: 'pos_quick_sale',
          ticket_image_url: null,
          created_at: orderRow.created_at || new Date().toISOString(),
          updated_at: orderRow.updated_at || new Date().toISOString()
        },
        current_order_items: [],
        status: 'occupied',
        order_channel: 'cashier',
        time_open_minutes: 0,
        item_count: cart.length
      } as TableWithOrder;
      setPayTable(syntheticTable);
      setShowPayModal(true);
    } catch (e: any) {
      console.error('[POS/Caja] quick sale error:', e);
      setError(e?.message || 'No se pudo registrar la venta.');
    } finally {
      setSubmitting(false);
    }
  };
  // ─── PayTableModal callback ───────────────────────────────
  const handlePaySuccess = (_allPaid: boolean) => {
    // El modal cierra solo cuando el usuario lo cierra; acá sólo
    // limpiamos el carrito y refrescamos productos (stock).
    setCart([]);
    setSuccessMsg('Venta registrada correctamente.');
    refetch();
  };
  const handlePayClose = async () => {
    // pos_quick_sale sin payments = intento abandonado, no pedido abierto real.
    //
    // Al tocar "Venta rápida" creamos la orden de inmediato (status='pending')
    // y abrimos PayTableModal. Si el cajero CIERRA el modal sin registrar
    // ningún pago, esa orden quedaría huérfana (pending, total_pagado=0) e
    // inflaría los contadores de "pedidos de mesa" del Dashboard para siempre.
    //
    // Regla: si la orden NO tiene ningún payment → la cancelamos
    // automáticamente con motivo "Venta rápida abandonada". Si SÍ tiene pagos
    // (parcial o total) → es una venta real y NO se toca.
    const orderId = payTable?.current_order?.id;
    setShowPayModal(false);
    setPayTable(null);
    if (!orderId || !restaurant?.id) return;
    try {
      const {
        count,
        error: payCountErr
      } = await supabase.from('payments').select('id', {
        count: 'exact',
        head: true
      }).eq('order_id', orderId);
      if (payCountErr) throw payCountErr;
      // Venta real con al menos un pago → respetar, no cancelar.
      if ((count ?? 0) > 0) return;
      // Orden huérfana sin pago → cancelar la orden y sus líneas.
      await supabase.from('orders').update({
        status: 'cancelled',
        notes: 'Venta rápida abandonada',
        updated_at: new Date().toISOString()
      }).eq('id', orderId);
      await supabase.from('order_items').update({
        status: 'cancelled'
      }).eq('order_id', orderId);
    } catch (e: any) {
      console.error('[POS/Caja] auto-cancel venta rápida abandonada error:', e);
    }
  };
  // ─── Active table picker ──────────────────────────────────
  const openTablePicker = async () => {
    if (!restaurant?.id) return;
    if (cart.length === 0) {
      setError('Agrega al menos un producto antes de elegir una mesa.');
      return;
    }
    setError(null);
    setSuccessMsg(null);
    setShowTablePicker(true);
    setTablesLoading(true);
    try {
      // `orders` NO tiene columna `table_number`: el número visible de la
      // mesa vive en `tables`, y la orden sólo guarda `table_id`. Pedirlo
      // directo devolvía 400 (column orders.table_number does not exist) y
      // el botón "Agregar a mesa activa" fallaba siempre. Se resuelve en
      // dos pasos para no depender de un join implícito.
      //
      // Estados abiertos: se excluyen únicamente 'delivered' y 'cancelled',
      // el mismo criterio que usa la grilla de Mesas y la vista de caja.
      const {
        data,
        error: e
      } = await supabase.from('orders').select('id, order_number, table_id, customer_name, total, status').eq('restaurant_id', Number(restaurant.id)).not('table_id', 'is', null).not('status', 'in', '(delivered,cancelled)').order('created_at', {
        ascending: false
      });
      if (e) throw e;
      const rows = data || [];
      const tableIds = Array.from(new Set(rows.map((r: any) => r.table_id).filter(Boolean)));
      const labelById = new Map<string, string>();
      if (tableIds.length > 0) {
        const {
          data: tableRows
        } = await supabase.from('tables').select('id, table_number, table_name').eq('restaurant_id', Number(restaurant.id)).in('id', tableIds);
        for (const t of tableRows || []) {
          const num = String((t as any).table_number || '').trim();
          const nm = String((t as any).table_name || '').trim();
          labelById.set(String((t as any).id), nm || num || 'Mesa');
        }
      }
      setActiveTables(rows.map((r: any) => ({
        orderId: String(r.id),
        tableNumber: labelById.get(String(r.table_id)) || 'Mesa',
        customerName: r.customer_name,
        total: r.total
      })));
    } catch (e: any) {
      console.error('[POS/Caja] active tables error:', e);
      setError(e?.message || 'No se pudieron cargar las mesas activas.');
      setActiveTables([]);
    } finally {
      setTablesLoading(false);
    }
  };
  const handleAddToTable = async (targetOrderId: string) => {
    if (!restaurant?.id) return;
    setSubmitting(true);
    setError(null);
    setSuccessMsg(null);
    try {
      // Insert order_items
      const itemsPayload = cart.map(l => ({
        order_id: targetOrderId,
        restaurant_id: Number(restaurant.id),
        menu_item_id: l.menuItemId,
        item_name_snapshot: l.name,
        unit_price_snapshot: l.unitPrice,
        quantity: l.quantity,
        line_total: l.unitPrice * l.quantity,
        status: 'pending',
        // Estantería nunca va a la cocina: se marca explícitamente para
        // que el puente de impresión la excluya de la comanda y la línea
        // quede sólo en la cuenta de la mesa.
        station_code: 'estanteria',
        notes: l.code ? `Cód. ${l.code}` : null
      }));
      const {
        error: itemsErr
      } = await supabase.from('order_items').insert(itemsPayload);
      if (itemsErr) throw itemsErr;
      // Recompute order totals
      const {
        data: allItems,
        error: fetchErr
      } = await supabase.from('order_items').select('line_total, status').eq('order_id', targetOrderId);
      if (fetchErr) throw fetchErr;
      const newSubtotal = (allItems || []).filter((it: any) => it.status !== 'cancelled').reduce((s: number, it: any) => s + (Number(it.line_total) || 0), 0);
      const {
        data: orderRow
      } = await supabase.from('orders').select('delivery_fee').eq('id', targetOrderId).maybeSingle();
      const deliveryFee = Number(orderRow?.delivery_fee) || 0;
      const {
        error: updErr
      } = await supabase.from('orders').update({
        subtotal: newSubtotal,
        total: newSubtotal + deliveryFee,
        updated_at: new Date().toISOString()
      }).eq('id', targetOrderId);
      if (updErr) throw updErr;
      setSuccessMsg('Productos agregados a la mesa.');
      setCart([]);
      setShowTablePicker(false);
      refetch();
    } catch (e: any) {
      console.error('[POS/Caja] add to table error:', e);
      setError(e?.message || 'No se pudieron agregar los productos a la mesa.');
    } finally {
      setSubmitting(false);
    }
  };
  // ─── Product CRUD handlers ────────────────────────────────
  const openNewProduct = () => {
    setProductError(null);
    setProductForm(EMPTY_PRODUCT_FORM);
    setShowProductForm(true);
  };
  const openEditProduct = (item: any) => {
    setProductError(null);
    setProductForm({
      id: String(item.id),
      name: item.name || '',
      price: item.price === null || item.price === undefined ? '' : String(item.price),
      code: item.external_pos_id ? String(item.external_pos_id) : '',
      stock: item.stock_quantity === null || item.stock_quantity === undefined ? '' : String(item.stock_quantity)
    });
    setShowProductForm(true);
  };
  const resolveRetailCategoryId = async (): Promise<string | null> => {
    if (!restaurant?.id) return null;
    // 1) Prefer the category an existing retail product already uses.
    const existingWithCat = items.find((it: any) => it.category_id);
    if (existingWithCat) return String((existingWithCat as any).category_id);
    // 2) Otherwise find the dedicated "Tienda / Productos de caja" category.
    const {
      data
    } = await supabase.from('menu_categories').select('id, name').eq('restaurant_id', Number(restaurant.id)).ilike('name', '%tienda%').limit(1);
    if (data && data.length > 0) return String(data[0].id);
    // 3) Fallback: any active category for this restaurant.
    const {
      data: anyCat
    } = await supabase.from('menu_categories').select('id').eq('restaurant_id', Number(restaurant.id)).order('sort_order', {
      ascending: true
    }).limit(1);
    return anyCat && anyCat.length > 0 ? String(anyCat[0].id) : null;
  };
  const handleSaveProduct = async () => {
    if (!restaurant?.id) return;
    const name = productForm.name.trim();
    const priceNum = Number(productForm.price);
    const stockRaw = productForm.stock.trim();
    const stockNum = stockRaw === '' ? null : Number(stockRaw);
    const code = productForm.code.trim() || null;
    if (!name) {
      setProductError('El nombre es obligatorio.');
      return;
    }
    if (!Number.isFinite(priceNum) || priceNum < 0) {
      setProductError('Ingresa un precio válido.');
      return;
    }
    if (stockNum !== null && (!Number.isFinite(stockNum) || stockNum < 0)) {
      setProductError('El stock debe ser un número válido.');
      return;
    }
    setSavingProduct(true);
    setProductError(null);
    try {
      // ── CÓDIGO DUPLICADO ────────────────────────────────────────────
      // `external_pos_id` es único por restaurante a nivel de base de
      // datos — INCLUYENDO productos desactivados, que no aparecen en
      // ningún listado del admin. Si alguien reutilizaba sin saberlo el
      // código de un producto viejo e inactivo, el guardado fallaba con
      // un error crudo de Postgres que no decía CUÁL producto ya lo
      // usaba. Acá se revisa antes de guardar y se avisa con nombre.
      if (code) {
        let dupeQuery = supabase.from('menu_items').select('id, name, is_active').eq('restaurant_id', Number(restaurant.id)).eq('external_pos_id', code);
        if (productForm.id) {
          dupeQuery = dupeQuery.neq('id', productForm.id);
        }
        const {
          data: dupeRows,
          error: dupeErr
        } = await dupeQuery.limit(1);
        if (dupeErr) {
          console.warn('[POS/Caja] duplicate code check failed (non-blocking):', dupeErr);
        } else if (dupeRows && dupeRows.length > 0) {
          const dupe = dupeRows[0] as any;
          throw new Error(`El código "${code}" ya lo usa "${dupe.name}"` + (dupe.is_active === false ? ' (producto desactivado, por eso no lo ves en la lista)' : '') + '. Usá otro código.');
        }
      }
      if (productForm.id) {
        // ── EDIT existing retail product ──
        const {
          error: updErr
        } = await supabase.from('menu_items').update({
          name,
          slug: slugify(name),
          price: priceNum,
          external_pos_id: code,
          stock_quantity: stockNum,
          updated_at: new Date().toISOString()
        }).eq('id', productForm.id);
        if (updErr) throw updErr;
        setSuccessMsg('Producto actualizado.');
      } else {
        // ── ADD new retail product ──
        const categoryId = await resolveRetailCategoryId();
        if (!categoryId) {
          throw new Error('No se encontró una categoría destino. Crea la categoría "Tienda" primero.');
        }
        const {
          error: insErr
        } = await supabase.from('menu_items').insert({
          restaurant_id: Number(restaurant.id),
          category_id: categoryId,
          name,
          slug: slugify(name),
          price: priceNum,
          external_pos_id: code,
          stock_quantity: stockNum,
          product_type: 'retail',
          show_in_pos: true,
          show_in_digital_menu: false,
          is_active: true,
          is_customizable: false,
          sort_order: 9999
        });
        if (insErr) throw insErr;
        setSuccessMsg('Producto agregado.');
      }
      setShowProductForm(false);
      setProductForm(EMPTY_PRODUCT_FORM);
      refetch();
    } catch (e: any) {
      console.error('[POS/Caja] save product error:', e);
      // Red de seguridad: si de todos modos llega el error crudo de
      // Postgres (ej. dos cajeros guardando al mismo tiempo), se traduce
      // acá a un mensaje entendible.
      const rawMsg = String(e?.message || '');
      const lower = rawMsg.toLowerCase();
      const friendlyMsg = lower.includes('menu_items_restaurant_external_pos_unique') || lower.includes('duplicate') && lower.includes('external_pos_id') ? 'Ese código ya está en uso por otro producto. Usá otro código.' : rawMsg || 'No se pudo guardar el producto.';
      setProductError(friendlyMsg);
    } finally {
      setSavingProduct(false);
    }
  };
  const handleDeleteProduct = async (item: any) => {
    if (!restaurant?.id) return;
    const ok = window.confirm(`¿Borrar "${item.name}" de la Estantería?\n\nEsto lo quita de la venta rápida. El historial de ventas no se afecta.`);
    if (!ok) return;
    setError(null);
    setSuccessMsg(null);
    try {
      // Soft-delete: marca inactivo para no romper el historial de
      // order_items que apunta a este producto (FK). El hook filtra
      // por is_active, así que desaparece de la Estantería al instante.
      const {
        error: delErr
      } = await supabase.from('menu_items').update({
        is_active: false,
        updated_at: new Date().toISOString()
      }).eq('id', item.id);
      if (delErr) throw delErr;
      setSuccessMsg('Producto borrado.');
      refetch();
    } catch (e: any) {
      console.error('[POS/Caja] delete product error:', e);
      setError(e?.message || 'No se pudo borrar el producto.');
    }
  };
  // ─── Loading / gating ─────────────────────────────────────
  if (restLoading || !restaurant) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-id="element-5944" data-mp="kb7oymmt7f75">
        <Skeleton className="w-16 h-16 rounded-full" data-id="element-5945" data-mp="2ssjuoguz0qc5" />
      </div>;
  }
  if (!isNatuCafe) {
    return <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-5946" data-mp="2nrmdo7lp62c8">
        <div className="p-6 flex flex-col items-center justify-center min-h-[50vh] text-center" data-id="element-5947" data-mp="2ivf6kbhkeksp">
          <Store className="w-16 h-16 text-gray-300 mb-4" data-id="element-5948" data-mp="2dwcc1kz7nwyz" />
          <h2 className="text-xl font-bold text-gray-900 mb-2" data-id="element-5949" data-mp="3bgyjqylq9uab">
            Módulo no disponible
          </h2>
          <p className="text-gray-500" data-id="element-5950" data-mp="36k83ob9xayow">
            Este módulo es exclusivo para NatuCafé.
          </p>
        </div>
      </AdminLayout>;
  }
  return <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-5951" data-mp="2wtdhf6wofz3g">
      <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-4" data-id="element-5952" data-mp="3u8rccrwvxdhi">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-5953" data-mp="3ph9dc9ef9mz6">
          <div data-id="element-5954" data-mp="1lj4uwh7mzcw">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-5955" data-mp="1m2oqiqf7xtk">Estantería</h1>
            <p className="text-sm text-gray-500" data-id="element-5956" data-mp="5m3363dis90m">
              Venta rápida de productos · {items.length} disponibles
            </p>
          </div>
          <button onClick={openNewProduct} className="h-11 px-4 rounded-xl font-bold text-sm bg-gray-900 hover:bg-gray-800 text-white active:scale-95 transition-all flex items-center justify-center gap-2 shrink-0" data-id="element-5957" data-mp="akecmczwkhib">
            <Plus className="w-4 h-4" data-id="element-5958" data-mp="okyvf95tidbt" />
            Agregar producto
          </button>
        </div>

        {/* Banners */}
        {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start gap-2" data-id="element-5959" data-mp="tcz2rmo7w8if">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" data-id="element-5960" data-mp="yd4okm7t30sl" />
            <span data-id="element-5961" data-mp="13bodmvhp4sp6">{error}</span>
          </div>}
        {successMsg && <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800 flex items-start gap-2" data-id="element-5962" data-mp="18aj48jlwnlju">
            <Check className="w-4 h-4 shrink-0 mt-0.5" data-id="element-5963" data-mp="illqs4mp5vcm" />
            <span data-id="element-5964" data-mp="dn0rq8j6lhpr">{successMsg}</span>
          </div>}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-id="element-5965" data-mp="dkejs7sdp070">
          {/* LEFT: products */}
          <div className="lg:col-span-2 space-y-3" data-id="element-5966" data-mp="scym567pf3ne">
            {/* Big search */}
            <div className="relative" data-id="element-5967" data-mp="nic6uryi4qy1">
              <Search className="w-5 h-5 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" data-id="element-5968" data-mp="11bailov94vnr" />
              <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por código o nombre… (ej: 1001, miel, café)" autoFocus className="w-full pl-12 pr-4 h-14 rounded-2xl border-2 border-gray-200 text-base font-medium focus:outline-none focus:border-amber-500 bg-white shadow-sm" data-id="element-5969" data-mp="wbp8ciamt0vq" />
              {search && <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-400" aria-label="Limpiar búsqueda" data-id="element-5970" data-mp="1nk8ubgnl74eo">
                  <X className="w-4 h-4" data-id="element-5971" data-mp="1itiqi7ut79m3" />
                </button>}
            </div>

            {/* Product grid */}
            <div className="bg-white rounded-2xl border border-gray-200 p-3 min-h-[300px]" data-id="element-5972" data-mp="14thogp2t5w51">
              {itemsLoading ? <div className="flex justify-center py-16 text-gray-400" data-id="element-5973" data-mp="18ww7lom1msmp">
                  <Loader2 className="w-6 h-6 animate-spin" data-id="element-5974" data-mp="uwlh4monbg22" />
                </div> : visibleItems.length === 0 ? <div className="text-center py-16 text-gray-500" data-id="element-5975" data-mp="zuwomqrgxeh4">
                  <Package className="w-10 h-10 mx-auto mb-2 text-gray-300" data-id="element-5976" data-mp="2pamd8zst9oyg" />
                  <p className="text-sm font-medium" data-id="element-5977" data-mp="2u97f13npogp0">
                    {search ? 'Sin resultados para esa búsqueda.' : 'No hay productos de Estantería cargados.'}
                  </p>
                </div> : <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-id="element-5978" data-mp="1d2llzg3n482k">
                  {visibleItems.map((item: any) => {
                const stock = item.stock_quantity === null || item.stock_quantity === undefined ? null : Number(item.stock_quantity);
                const lowStock = stock !== null && stock <= 0;
                return <div key={item.id} className="p-3 rounded-xl border border-gray-200 flex flex-col gap-2 bg-white" data-id="element-5979" data-mp="pbg5qztsfdrq">
                        <div className="flex items-start justify-between gap-2" data-id="element-5980" data-mp="2jnmuz2jinv31">
                          <span className="text-[10px] font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded tabular-nums" data-id="element-5981" data-mp="2ep1ur349lbel">
                            {item.external_pos_id || '—'}
                          </span>
                          {stock !== null && <span className={cn('text-[10px] font-bold px-2 py-0.5 rounded tabular-nums', lowStock ? 'text-red-700 bg-red-50' : 'text-emerald-700 bg-emerald-50')} data-id="element-5982" data-mp="2r5ugtrttleth">
                              Stock {stock}
                            </span>}
                        </div>
                        <p className="font-bold text-sm text-gray-900 leading-snug line-clamp-2 min-h-[2.5rem]" data-id="element-5983" data-mp="2ha9ic3nymwoh">
                          {item.name}
                        </p>
                        <p className="text-base font-black text-gray-900 tabular-nums" data-id="element-5984" data-mp="3sowkhyuebuud">
                          {formatCurrency(item.price)}
                        </p>
                        <div className="mt-auto flex items-center gap-1.5" data-id="element-5985" data-mp="nnobl8u8zqbf">
                          <button onClick={() => addToCart(item)} className="flex-1 h-9 rounded-lg bg-amber-500 hover:bg-amber-600 text-white font-bold text-sm flex items-center justify-center gap-1 active:scale-95 transition-all" data-id="element-5986" data-mp="sk5229ox6gro">
                            <Plus className="w-4 h-4" data-id="element-5987" data-mp="4x6mrfcjdevc" />
                            Agregar
                          </button>
                          <button onClick={() => openEditProduct(item)} className="w-9 h-9 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-600 flex items-center justify-center active:scale-95 transition-all shrink-0" aria-label={`Editar ${item.name}`} data-id="element-5988" data-mp="3r2br72krm87v">
                            <Pencil className="w-4 h-4" data-id="element-5989" data-mp="1u7xm9cp571hx" />
                          </button>
                          <button onClick={() => handleDeleteProduct(item)} className="w-9 h-9 rounded-lg border border-gray-200 hover:bg-red-50 hover:border-red-200 text-red-500 flex items-center justify-center active:scale-95 transition-all shrink-0" aria-label={`Borrar ${item.name}`} data-id="element-5990" data-mp="3epztpgq93sa0">
                            <Trash2 className="w-4 h-4" data-id="element-5991" data-mp="1f97a3txsnka" />
                          </button>
                        </div>
                      </div>;
              })}
                </div>}
            </div>
          </div>

          {/* RIGHT: cart */}
          <div className="bg-white rounded-2xl border border-gray-200 flex flex-col max-h-[calc(100vh-180px)] sticky top-4" data-id="element-5992" data-mp="6d2b4q7rp5he">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between" data-id="element-5993" data-mp="2d4ju6yn67w0i">
              <div className="flex items-center gap-2" data-id="element-5994" data-mp="285yseus9t49y">
                <ShoppingBag className="w-4 h-4 text-gray-700" data-id="element-5995" data-mp="c1sr3dzvs7js" />
                <p className="font-bold text-sm text-gray-900" data-id="element-5996" data-mp="gz2e6mzss74g">
                  Carrito ({cartCount})
                </p>
              </div>
              {cart.length > 0 && <button onClick={() => setCart([])} className="text-xs font-bold text-red-600 hover:text-red-700" data-id="element-5997" data-mp="q090jcp4oc5m">
                  Vaciar
                </button>}
            </div>

            <div className="flex-1 overflow-y-auto p-3 space-y-2" data-id="element-5998" data-mp="zx58nhb46w9n">
              {cart.length === 0 ? <div className="text-center py-12 text-gray-400 text-sm" data-id="element-5999" data-mp="14pommfdprtez">
                  Selecciona productos para iniciar una venta.
                </div> : cart.map(l => <div key={l.id} className="rounded-lg border border-gray-200 px-3 py-2" data-id="element-6000" data-mp="1dnqjqjubu1wz">
                    <div className="flex items-start justify-between gap-2 mb-1.5" data-id="element-6001" data-mp="1st4qqfasnnef">
                      <div className="min-w-0" data-id="element-6002" data-mp="1nu1n8zagpp8k">
                        {l.code && <span className="text-[10px] font-bold text-amber-700 tabular-nums" data-id="element-6003" data-mp="1iuovq18nbtzn">
                            {l.code}
                          </span>}
                        <p className="text-sm font-bold text-gray-900 leading-tight truncate" data-id="element-6004" data-mp="1evg2cxhipy4m">
                          {l.name}
                        </p>
                      </div>
                      <button onClick={() => removeLine(l.id)} className="text-red-500 hover:bg-red-50 w-7 h-7 rounded-md flex items-center justify-center shrink-0" aria-label="Quitar" data-id="element-6005" data-mp="26mchy5uyqgvl">
                        <Trash2 className="w-3.5 h-3.5" data-id="element-6006" data-mp="2ubf15i58whs7" />
                      </button>
                    </div>
                    <div className="flex items-center justify-between" data-id="element-6007" data-mp="2ph0xksgfj4cg">
                      <div className="flex items-center gap-1" data-id="element-6008" data-mp="2a0ci9yxe5klp">
                        <button onClick={() => updateQty(l.id, -1)} className="w-7 h-7 rounded-md bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-700" data-id="element-6009" data-mp="2f0qe8ilpx43d">
                          <Minus className="w-3.5 h-3.5" data-id="element-6010" data-mp="252auj0p0sx3w" />
                        </button>
                        <span className="w-7 text-center font-black text-sm text-gray-900 tabular-nums" data-id="element-6011" data-mp="2ssdmzhwuk531">
                          {l.quantity}
                        </span>
                        <button onClick={() => updateQty(l.id, +1)} className="w-7 h-7 rounded-md bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-700" data-id="element-6012" data-mp="2jy8rgl6rq5h6">
                          <Plus className="w-3.5 h-3.5" data-id="element-6013" data-mp="3gh69knrkx0kz" />
                        </button>
                      </div>
                      <span className="text-sm font-black text-gray-900 tabular-nums" data-id="element-6014" data-mp="151paubtdrred">
                        {formatCurrency(l.unitPrice * l.quantity)}
                      </span>
                    </div>
                  </div>)}
            </div>

            <div className="border-t border-gray-100 p-4 space-y-3 bg-gray-50 rounded-b-2xl" data-id="element-6015" data-mp="19sxjv6qstar0">
              <div className="flex items-center justify-between" data-id="element-6016" data-mp="19va2zn8alr5n">
                <span className="text-sm font-bold text-gray-600" data-id="element-6017" data-mp="m3v3e27whny3">Total</span>
                <span className="text-2xl font-black text-gray-900 tabular-nums" data-id="element-6018" data-mp="h5bh20peaa57">
                  {formatCurrency(cartTotal)}
                </span>
              </div>
              <button onClick={handleQuickSale} disabled={submitting || cart.length === 0} className="w-full h-12 rounded-xl font-black text-sm bg-amber-500 hover:bg-amber-600 text-white active:scale-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-6019" data-mp="r2plf1y1n8h5">
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" data-id="element-6020" data-mp="2ejcusagdm0av" /> : <>
                    <Receipt className="w-5 h-5" data-id="element-6021" data-mp="2jiph8u3c55cx" />
                    Venta rápida
                  </>}
              </button>
              <button onClick={openTablePicker} disabled={submitting || cart.length === 0} className="w-full h-11 rounded-xl font-bold text-sm bg-white border-2 border-gray-200 hover:border-gray-300 text-gray-900 active:scale-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-6022" data-mp="2ohah2seodn0u">
                <Users className="w-4 h-4" data-id="element-6023" data-mp="25kipzwsw63m8" />
                Agregar a mesa activa
              </button>
            </div>
          </div>
        </div>

        {/* Active table picker modal */}
        {showTablePicker && <div className="fixed inset-0 z-[120] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => !submitting && setShowTablePicker(false)} data-id="element-6024" data-mp="1d0ka372fgr66">
            <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[80vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-6025" data-mp="3hbysnitiysat">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between" data-id="element-6026" data-mp="37fc8kw90w59u">
                <div data-id="element-6027" data-mp="32msn7d43g5uh">
                  <h3 className="font-black text-gray-900" data-id="element-6028" data-mp="32maugqgn49rb">Mesas activas</h3>
                  <p className="text-xs text-gray-500" data-id="element-6029" data-mp="2ymsapy0k24q2">
                    Selecciona una mesa para sumar los productos.
                  </p>
                </div>
                <button onClick={() => setShowTablePicker(false)} disabled={submitting} className="w-9 h-9 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-500" data-id="element-6030" data-mp="2oocgvna58hcv">
                  <X className="w-5 h-5" data-id="element-6031" data-mp="cbf16vml4gdu" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-3" data-id="element-6032" data-mp="gel69zxyo1iq">
                {tablesLoading ? <div className="flex justify-center py-12 text-gray-400" data-id="element-6033" data-mp="2etu84r08v7d">
                    <Loader2 className="w-6 h-6 animate-spin" data-id="element-6034" data-mp="7ddjae0wan3y" />
                  </div> : activeTables.length === 0 ? <div className="text-center py-12 text-gray-500 text-sm" data-id="element-6035" data-mp="3ostqokqbxlzt">
                    No hay mesas con orden abierta.
                  </div> : <div className="space-y-2" data-id="element-6036" data-mp="3fvjcorm4nir7">
                    {activeTables.map(t => <button key={t.orderId} onClick={() => handleAddToTable(t.orderId)} disabled={submitting} className="w-full text-left p-3 rounded-xl border border-gray-200 hover:border-amber-400 hover:bg-amber-50/40 active:scale-[0.99] transition-all flex items-center justify-between gap-3 disabled:opacity-40" data-id="element-6037" data-mp="3ku4g0zwoeijv">
                        <div className="min-w-0" data-id="element-6038" data-mp="2na4j7j0crm0o">
                          <p className="font-bold text-sm text-gray-900" data-id="element-6039" data-mp="318knz4enn2g6">
                            Mesa {t.tableNumber ?? '—'}
                          </p>
                          {t.customerName && <p className="text-xs text-gray-500 truncate" data-id="element-6040" data-mp="3a6woby5bpelt">
                              {t.customerName}
                            </p>}
                        </div>
                        {t.total !== null && <span className="text-sm font-black text-gray-900 tabular-nums shrink-0" data-id="element-6041" data-mp="3ez7q76jmjah0">
                            {formatCurrency(Number(t.total) || 0)}
                          </span>}
                      </button>)}
                  </div>}
              </div>
            </div>
          </div>}

        {/* Pay modal — Venta rápida con factura/método de pago */}
        <PayTableModal isOpen={showPayModal} onClose={handlePayClose} table={payTable} restaurantId={restaurant?.id} onSuccess={handlePaySuccess} data-id="element-6042" data-mp="3rp614jii0lyg" />

        {/* Product add/edit modal */}
        {showProductForm && <div className="fixed inset-0 z-[120] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => !savingProduct && setShowProductForm(false)} data-id="element-6043" data-mp="j6qdu793oz08">
            <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[90vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-6044" data-mp="t564ya84o89q">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between" data-id="element-6045" data-mp="2uz5fgtdfelld">
                <div data-id="element-6046" data-mp="2q0kc4l2vnlsp">
                  <h3 className="font-black text-gray-900" data-id="element-6047" data-mp="15fnee8w2qfza">
                    {productForm.id ? 'Editar producto' : 'Agregar producto'}
                  </h3>
                  <p className="text-xs text-gray-500" data-id="element-6048" data-mp="1ahcpfojaqtnd">Estantería · NatuCafé</p>
                </div>
                <button onClick={() => setShowProductForm(false)} disabled={savingProduct} className="w-9 h-9 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-500" aria-label="Cerrar" data-id="element-6049" data-mp="voax2b7wj87i">
                  <X className="w-5 h-5" data-id="element-6050" data-mp="rmpdk70v3voc" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-5 space-y-4" data-id="element-6051" data-mp="ctcd0w0n8b1f">
                {productError && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start gap-2" data-id="element-6052" data-mp="hsp11k393o5l">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" data-id="element-6053" data-mp="2c4dj3v4s46u7" />
                    <span data-id="element-6054" data-mp="2c2j6r57kpn1q">{productError}</span>
                  </div>}
                <div data-id="element-6055" data-mp="2aorlwb6bwtff">
                  <label className="block text-xs font-bold text-gray-700 mb-1" data-id="element-6056" data-mp="2al3txggj8f2t">
                    Nombre <span className="text-red-500" data-id="element-6057" data-mp="25qgdm4vomqzb">*</span>
                  </label>
                  <input type="text" value={productForm.name} onChange={e => setProductForm(f => ({
                ...f,
                name: e.target.value
              }))} placeholder="Ej: Café molido 500g" autoFocus className="w-full h-11 px-3 rounded-lg border border-gray-300 focus:outline-none focus:border-amber-500 text-sm" data-id="element-6058" data-mp="20pkahtj5n8uy" />
                </div>
                <div className="grid grid-cols-2 gap-3" data-id="element-6059" data-mp="3c5h9ybomdb6f">
                  <div data-id="element-6060" data-mp="37dqw3vqh45a9">
                    <label className="block text-xs font-bold text-gray-700 mb-1" data-id="element-6061" data-mp="37d7c89h9j6tl">
                      Precio <span className="text-red-500" data-id="element-6062" data-mp="tvcrkcjbiw9q">*</span>
                    </label>
                    <input type="number" inputMode="decimal" min={0} value={productForm.price} onChange={e => setProductForm(f => ({
                  ...f,
                  price: e.target.value
                }))} placeholder="0" className="w-full h-11 px-3 rounded-lg border border-gray-300 focus:outline-none focus:border-amber-500 text-sm tabular-nums" data-id="element-6063" data-mp="yu60rsrko143" />
                  </div>
                  <div data-id="element-6064" data-mp="1qkalxtn5os6d">
                    <label className="block text-xs font-bold text-gray-700 mb-1" data-id="element-6065" data-mp="1qkkbxh87azc2">
                      Stock
                    </label>
                    <input type="number" inputMode="numeric" min={0} value={productForm.stock} onChange={e => setProductForm(f => ({
                  ...f,
                  stock: e.target.value
                }))} placeholder="Opcional" className="w-full h-11 px-3 rounded-lg border border-gray-300 focus:outline-none focus:border-amber-500 text-sm tabular-nums" data-id="element-6066" data-mp="20j9vrfjnqtuy" />
                  </div>
                </div>
                <div data-id="element-6067" data-mp="z0oka3gbqyxv">
                  <label className="block text-xs font-bold text-gray-700 mb-1" data-id="element-6068" data-mp="z181tj22blbd">
                    Código
                  </label>
                  <input type="text" value={productForm.code} onChange={e => setProductForm(f => ({
                ...f,
                code: e.target.value
              }))} placeholder="Ej: 1001 (opcional)" className="w-full h-11 px-3 rounded-lg border border-gray-300 focus:outline-none focus:border-amber-500 text-sm tabular-nums" data-id="element-6069" data-mp="18wrxq5gmmk04" />
                  <p className="text-[11px] text-gray-400 mt-1" data-id="element-6070" data-mp="2iwm18udf3nya">
                    Código interno para buscar el producto en caja.
                  </p>
                </div>
              </div>
              <div className="border-t border-gray-100 p-4 flex items-center gap-2 bg-gray-50" data-id="element-6071" data-mp="3amqsnczlhb90">
                <button onClick={() => setShowProductForm(false)} disabled={savingProduct} className="flex-1 h-11 rounded-xl font-bold text-sm bg-white border-2 border-gray-200 hover:border-gray-300 text-gray-700 active:scale-95 transition-all disabled:opacity-40" data-id="element-6072" data-mp="3fjz3esieyd08">
                  Cancelar
                </button>
                <button onClick={handleSaveProduct} disabled={savingProduct} className="flex-1 h-11 rounded-xl font-black text-sm bg-amber-500 hover:bg-amber-600 text-white active:scale-95 transition-all disabled:opacity-40 flex items-center justify-center gap-2" data-id="element-6073" data-mp="27fduaap3679">
                  {savingProduct ? <Loader2 className="w-4 h-4 animate-spin" data-id="element-6074" data-mp="3u8ima48aga8l" /> : <>
                      <Check className="w-4 h-4" data-id="element-6075" data-mp="3p9625r8sxh9p" />
                      {productForm.id ? 'Guardar' : 'Agregar'}
                    </>}
                </button>
              </div>
            </div>
          </div>}
      </div>
    </AdminLayout>;
}